import { useEffect } from 'react';

/**
 * Prevent the page behind a modal from moving while leaving the modal's own
 * scroll container untouched. Every inline value is restored exactly so this
 * composes safely with a host page that already manages document scrolling.
 */
export function useDocumentScrollLock(locked: boolean) {
  useEffect(() => {
    if (!locked) return;

    const root = document.documentElement;
    const body = document.body;
    const scrollX = window.scrollX;
    const scrollY = window.scrollY;
    const previous = {
      rootOverflow: root.style.overflow,
      rootScrollbarGutter: root.style.scrollbarGutter,
      bodyOverflow: body.style.overflow,
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyLeft: body.style.left,
      bodyRight: body.style.right,
      bodyWidth: body.style.width,
    };

    // Keeping a stable gutter prevents the pathway from shifting sideways when
    // the browser's page scrollbar disappears. Pinning the body prevents wheel
    // and touch gestures over the backdrop from changing the page offset in
    // browsers that still allow scrolling with overflow hidden.
    root.style.scrollbarGutter = 'stable';
    root.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = `-${scrollX}px`;
    body.style.right = '0';
    body.style.width = '100%';

    return () => {
      root.style.overflow = previous.rootOverflow;
      root.style.scrollbarGutter = previous.rootScrollbarGutter;
      body.style.overflow = previous.bodyOverflow;
      body.style.position = previous.bodyPosition;
      body.style.top = previous.bodyTop;
      body.style.left = previous.bodyLeft;
      body.style.right = previous.bodyRight;
      body.style.width = previous.bodyWidth;

      if (scrollX !== 0 || scrollY !== 0) {
        window.scrollTo(scrollX, scrollY);
      }
    };
  }, [locked]);
}
